<template>
  <div class="question-columns-layout">
    <div v-if="hasTopColumn" class="question-column question-column-top">
      <slot name="top" />
    </div>
    <div
      v-if="hasLeftRightColumns"
      class="question-columns-content"
      :class="{ 'question-two-columns': alignTwoColumns }"
    >
      <div
        v-if="hasLeftColumn"
        class="question-column question-column-left"
        :class="columnCssClasses.left"
      >
        <div ref="leftNavigatorEl" class="scroll-wrapper" @scroll="handleLeftScroll">
          <slot name="left" />
        </div>
      </div>
      <div
        v-if="hasRightColumn"
        class="question-column question-column-right"
        :class="columnCssClasses.right"
      >
        <div ref="rightNavigatorEl" class="scroll-wrapper" @scroll="handleRightScroll">
          <slot name="right" />
        </div>
      </div>
    </div>
    <div v-if="hasBottomColumn" class="question-column question-column-bottom">
      <slot name="bottom" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useSlots } from "vue";
import { useScrollNearEnd } from "@utils/scroll.utils";

const slots = useSlots();

interface Props {
  alignTwoColumns?: boolean;
}

const leftNavigatorEl = ref<HTMLDivElement | null>(null);
const rightNavigatorEl = ref<HTMLDivElement | null>(null);

const {
  hasOverflow: hasLeftOverflow,
  isScrolledNearEnd: isLeftScrolledNearEnd,
  updateScrollMetrics: updateLeftScrollMetrics,
} = useScrollNearEnd(leftNavigatorEl, {
  axis: "vertical",
});

const {
  hasOverflow: hasRightOverflow,
  isScrolledNearEnd: isRightScrolledNearEnd,
  updateScrollMetrics: updateRightScrollMetrics,
} = useScrollNearEnd(rightNavigatorEl, {
  axis: "vertical",
});

const handleLeftScroll = (): void => {
  if (!leftNavigatorEl.value) {
    return;
  }

  updateLeftScrollMetrics();
};

const handleRightScroll = (): void => {
  if (!rightNavigatorEl.value) {
    return;
  }

  updateRightScrollMetrics();
};

const buildColumnCssClasses = (
  hasOverflow: boolean,
  isScrolledNearEnd: boolean,
): Record<string, boolean> => ({
  "column-scroll-overflow": hasOverflow,
  "column-scroll-end": isScrolledNearEnd,
});

const columnCssClasses = computed(() => ({
  left: buildColumnCssClasses(hasLeftOverflow.value, isLeftScrolledNearEnd.value),
  right: buildColumnCssClasses(hasRightOverflow.value, isRightScrolledNearEnd.value),
}));

withDefaults(defineProps<Props>(), {
  alignTwoColumns: false,
});

const hasTopColumn = computed(() => !!slots.top);
const hasLeftColumn = computed(() => !!slots.left);
const hasRightColumn = computed(() => !!slots.right);
const hasBottomColumn = computed(() => !!slots.bottom);
const hasLeftRightColumns = computed(() => hasLeftColumn.value || hasRightColumn.value);

onMounted(async (): Promise<void> => {
  await nextTick();
  handleLeftScroll();
  handleRightScroll();
});

defineOptions({ name: "QuestionColumnsLayout" });
</script>

<style lang="scss">
@use "@styles/abstracts/mixins" as *;
@use "@styles/layout/breakpoint" as *;
@use "@styles/layout/z-map" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;

.question-columns-layout {
  .question-columns-content {
    &.question-two-columns {
      display: grid;
      grid-template-columns: 1fr;
      min-height: 0;

      .question-column-left,
      .question-column-right {
        position: relative;
        min-width: 0;

        &.column-scroll-overflow {
          &::after {
            @include position-mixin(
              $width: 100%,
              $height: 40px,
              $left: 0,
              $bottom: 0,
              $pseudo: true
            );
            background: $module-column-gradient;
            transition: opacity 0.3s ease-in-out;
            opacity: 1;
            pointer-events: none;
            z-index: z-map("default");
          }

          .scroll-wrapper {
            padding-right: $spacing-03; // add spacing to give room between content and scrollbar
          }
        }

        &.column-scroll-end {
          &::after {
            opacity: 0;
          }
        }
      }

      @include media("desktop") {
        grid-template-columns: 1fr 1fr;
        gap: $spacing-08;
        margin-top: $spacing-07;
        margin-bottom: $spacing-07;

        .scroll-wrapper {
          max-height: calc((var(--vh, 1vh) * 75));
          min-height: 100%;
          overflow: auto;

          .is-side-by-side-layout.edit & {
            max-height: none;
            overflow: unset;
          }

          // disable scrollable content in child components
          .question-column {
            .scroll-wrapper {
              max-height: none;
              overflow: unset;
            }
          }
        }

        .column-scroll-overflow {
          .is-side-by-side-layout.edit & {
            &::after {
              display: none;
            }
          }
        }

        .question-column {
          .scroll-wrapper {
            > :first-child {
              margin-top: 0;
            }

            > :last-child {
              margin-bottom: 0;
            }
          }
        }
      }
    }
  }
}
</style>
